export interface OnboardingState {
    wizardCompleted: boolean;
    seenRoutes: string[];
    loadedStepIds: string[];
    shownStepIds: string[];
    completedStepIds: string[];
    seenStepIds: string[];
    updatedAt: string;
}

const DEFAULT_STATE: OnboardingState = {
    wizardCompleted: false,
    seenRoutes: [],
    loadedStepIds: [],
    shownStepIds: [],
    completedStepIds: [],
    seenStepIds: [],
    updatedAt: new Date(0).toISOString(),
};

const ONBOARDING_STORAGE_PREFIX = 'oscar:onboarding';
const LEGACY_ONBOARDING_STORAGE_VERSION = 'v1';
export const ONBOARDING_STORAGE_VERSION = 'v2';

const normalizeStringArray = (value: unknown): string[] =>
    Array.isArray(value)
        ? value.filter((entry: unknown): entry is string => typeof entry === 'string')
        : [];

const parseOnboardingState = (raw: string | null): OnboardingState => {
    if (!raw) {
        return DEFAULT_STATE;
    }

    try {
        const parsed = JSON.parse(raw);
        const legacySeenStepIds = normalizeStringArray(parsed?.seenStepIds);
        const completedStepIds = normalizeStringArray(parsed?.completedStepIds);

        return {
            wizardCompleted: parsed?.wizardCompleted === true,
            seenRoutes: normalizeStringArray(parsed?.seenRoutes),
            loadedStepIds: normalizeStringArray(parsed?.loadedStepIds),
            shownStepIds: normalizeStringArray(parsed?.shownStepIds),
            completedStepIds: completedStepIds.length > 0 ? completedStepIds : legacySeenStepIds,
            seenStepIds: legacySeenStepIds,
            updatedAt: typeof parsed?.updatedAt === 'string' ? parsed.updatedAt : DEFAULT_STATE.updatedAt,
        };
    } catch {
        return DEFAULT_STATE;
    }
};

const mergeUnique = (...lists: Array<string[] | undefined>): string[] => {
    const merged = new Set<string>();
    lists.forEach((list) => {
        (list || []).forEach((item) => {
            if (item) {
                merged.add(item);
            }
        });
    });
    return Array.from(merged);
};

const mergeOnboardingStates = (states: OnboardingState[]): OnboardingState => {
    if (states.length === 0) {
        return DEFAULT_STATE;
    }

    return {
        wizardCompleted: states.some((state) => state.wizardCompleted),
        seenRoutes: mergeUnique(...states.map((state) => state.seenRoutes)),
        loadedStepIds: mergeUnique(...states.map((state) => state.loadedStepIds)),
        shownStepIds: mergeUnique(...states.map((state) => state.shownStepIds)),
        completedStepIds: mergeUnique(...states.map((state) => state.completedStepIds)),
        seenStepIds: mergeUnique(...states.map((state) => state.seenStepIds)),
        updatedAt: states
            .map((state) => state.updatedAt)
            .filter((value): value is string => typeof value === 'string' && value.length > 0)
            .sort()
            .pop() || DEFAULT_STATE.updatedAt,
    };
};

const readLegacyOnboardingState = (storageKey?: string | null): OnboardingState => {
    if (!storageKey || typeof window === 'undefined') {
        return DEFAULT_STATE;
    }

    const match = storageKey.match(
        new RegExp(`^${ONBOARDING_STORAGE_PREFIX}:${ONBOARDING_STORAGE_VERSION}:(\\d+)$`)
    );
    const userId = match?.[1];
    if (!userId) {
        return DEFAULT_STATE;
    }

    try {
        const prefix = `${ONBOARDING_STORAGE_PREFIX}:${LEGACY_ONBOARDING_STORAGE_VERSION}:${userId}:`;
        const legacyStates: OnboardingState[] = [];

        for (let index = 0; index < window.localStorage.length; index += 1) {
            const key = window.localStorage.key(index);
            if (!key || !key.startsWith(prefix)) {
                continue;
            }
            legacyStates.push(parseOnboardingState(window.localStorage.getItem(key)));
        }

        return mergeOnboardingStates(legacyStates);
    } catch {
        return DEFAULT_STATE;
    }
};

export const buildOnboardingStorageKey = (
    userId?: number | null,
    _companyId?: number | null
): string | null => {
    if (!userId) return null;
    return `${ONBOARDING_STORAGE_PREFIX}:${ONBOARDING_STORAGE_VERSION}:${userId}`;
};

export const readOnboardingState = (storageKey?: string | null): OnboardingState => {
    if (!storageKey || typeof window === 'undefined') {
        return DEFAULT_STATE;
    }
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (raw) {
            return parseOnboardingState(raw);
        }
        return readLegacyOnboardingState(storageKey);
    } catch {
        return DEFAULT_STATE;
    }
};

export const writeOnboardingState = (storageKey: string, state: OnboardingState): void => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
        // Ignore storage errors (private mode / quota).
    }
};

export const mergeOnboardingState = (
    storageKey: string | null,
    updater: (current: OnboardingState) => OnboardingState
): OnboardingState => {
    const current = readOnboardingState(storageKey);
    const next = updater(current);
    if (storageKey) {
        writeOnboardingState(storageKey, {
            ...next,
            updatedAt: new Date().toISOString(),
        });
    }
    return next;
};
